import {
  SparklesIcon,
  ArrowUpTrayIcon,
  MicrophoneIcon,
  DocumentMagnifyingGlassIcon
} from "@heroicons/react/24/outline";
import axios from "axios";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-h5-audio-player/lib/styles.css";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { useAuth } from "../../context/AuthContext";
import { CaseScore, PendingSuggestionsChanges, PolicyReference, Suggestion } from "../../db/schema";
import Button from "../common/Button";
import Tooltip from "../common/Tooltip";
import { toastOptions } from "../common/toastOptions";
import PendingChangesBanner from "./PendingChangesBanner";
import ReportScore from "./ReportScore";
import { parseSuggestionTag } from "./suggestionTags";
import SuggestionTagBadges from "./SuggestionTagBadges";
import SuggestionsReview from "./SuggestionsReview";
import type { SuggestionChange } from "./SuggestionsReview";
import AudioPlayback from "../Menu/AudioPlayback";
import UploadModal from "../Menu/UploadModal";
import Toggle from "../Settings/Toggle";
import { processAudioFile } from "../../services/audioProcessingService";

function extractPlaceholders(text: string): string[] {
  const regex = /\{\{[^}]+\}\}/g;
  const matches = text.match(regex);
  return matches ? [...new Set(matches)] : [];
}

// Filter out changes that can't be applied (placeholder/original_text not found in editor)
function filterApplicableChanges(
  changes: SuggestionChange[],
  editor: any,
): { applicable: SuggestionChange[]; skipped: number } {
  if (!editor) return { applicable: changes, skipped: 0 };
  const docText = editor.state.doc.textContent || "";
  const applicable = changes.filter((change) => {
    if (change.type === "placeholder_fill") {
      return change.placeholder ? docText.includes(change.placeholder) : false;
    }
    if (change.type === "sentence_rewrite") {
      return change.original_text ? docText.includes(change.original_text) : false;
    }
    // content_addition is always applicable (has fallback to end-of-doc)
    return true;
  });
  return { applicable, skipped: changes.length - applicable.length };
}

interface SuggestionsProps {
  caseSuggestions?: Suggestion[];
  caseScore?: CaseScore | null;
  caseId?: string;
  editorContent?: string;
  isCaseLoading?: boolean;
  setCaseSuggestions?: React.Dispatch<React.SetStateAction<Suggestion[]>>;
  setCaseScore?: React.Dispatch<React.SetStateAction<CaseScore | undefined>>;
  onRecordComplete?: () => void;
  onSaveEditor?: () => void;
  isAddingToReport?: boolean;
  setIsAddingToReport?: (value: boolean) => void;
  // Set to true the moment the user starts an Add to Report upload, flipped
  // back to false by audioProcessingService once the local upload phase
  // finishes (via the pnai:add-to-report-upload-complete window event the
  // editor listens for). The overlay uses this to differentiate
  // "Uploading audio" (must stay) vs "Generating changes" (can leave).
  setIsUploadingAddToReport?: (value: boolean) => void;
  // Persisted "Add to Report is processing" flag derived from
  // caseAudioMetadata.intent. Survives page refresh — used to keep the Add
  // to Report and Re-evaluate buttons disabled while the server-side batch
  // is still in flight, even if the local isAddingToReport flag was lost.
  isAddToReportInFlight?: boolean;
  editor?: any;
  // Server-persisted "Add to Report" review state. Set when an Add-to-Report
  // batch finishes processing and Bedrock returns its discrete changes. The
  // success path auto-opens the review walkthrough; failures show a small
  // error banner with Retry/Discard.
  pendingSuggestionsChanges?: PendingSuggestionsChanges | null;
  // Called after pending changes are accepted or discarded so the parent can
  // refresh case state.
  onPendingChangesCleared?: () => void;
  // Lifted lock for the editor while the review walkthrough is active. The
  // canonical editable effect in TiptapEditor watches this and keeps the
  // editor read-only the entire time the review is open.
  setIsReviewingChanges?: (value: boolean) => void;
  // The policy sections this case retrieved. A policy suggestion's source
  // badge opens the matching one in the sidebar via onOpenPolicy.
  policyReferences?: PolicyReference[];
  // `reference` is the matching retrieved section when the case stored one;
  // otherwise the parent resolves the label itself.
  onOpenPolicy?: (label: string, reference?: PolicyReference) => void;
  // "Hide placeholders": collapses the {{…}} chips in the editor and keeps
  // them out of exports. State lives in Editor so the export path sees it.
  placeholdersHidden?: boolean;
  onTogglePlaceholdersHidden?: () => void;
}

function Suggestions({
  caseSuggestions,
  caseScore,
  caseId,
  editorContent,
  isCaseLoading,
  setCaseSuggestions,
  setCaseScore,
  onRecordComplete,
  onSaveEditor,
  isAddingToReport,
  setIsAddingToReport,
  setIsUploadingAddToReport,
  isAddToReportInFlight,
  editor,
  pendingSuggestionsChanges,
  onPendingChangesCleared,
  setIsReviewingChanges,
  policyReferences,
  onOpenPolicy,
  placeholdersHidden,
  onTogglePlaceholdersHidden,
}: SuggestionsProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [score, setScore] = useState<CaseScore | null | undefined>(undefined);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const hasAlreadyBeenEvaluated = score !== undefined && suggestions.length > 0;
  const { checkAuthStatus } = useAuth();

  // Suggestions recording/upload state
  const recordModalRef = useRef<HTMLDialogElement>(null);
  const uploadModalRef = useRef<HTMLDialogElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Active in-review state — populated when the user clicks "Review" on the
  // banner. The source of truth for the changes lives in
  // pendingSuggestionsChanges (server-persisted), this is just the filtered
  // working copy for the current walkthrough.
  const [pendingChanges, setPendingChanges] = useState<SuggestionChange[]>([]);
  const [pendingCaseFileIds, setPendingCaseFileIds] = useState<string[]>([]);
  const [isReviewing, setIsReviewing] = useState(false);
  // Tracks the createdAt of the pending blob we've already opened a review
  // for. Used by the auto-open effect below to avoid reopening the same blob
  // during the brief window between local state clear and parent refetch
  // after the user accepts or discards a review.
  const lastHandledPendingCreatedAtRef = useRef<string | null>(null);

  const hasPendingError = !!pendingSuggestionsChanges?.error;
  const hasPendingEmpty =
    !!pendingSuggestionsChanges &&
    !pendingSuggestionsChanges.error &&
    !pendingSuggestionsChanges.changes?.length;
  const hasPendingChanges =
    !!pendingSuggestionsChanges &&
    !pendingSuggestionsChanges.error &&
    !hasPendingEmpty;

  const placeholders = useMemo(
    () => (editorContent ? extractPlaceholders(editorContent) : []),
    [editorContent],
  );

  const [hasEvidenceIntegration, setHasEvidenceIntegration] = useState(false);
  useEffect(() => {
    axios
      .get("/api/settings/evidence-integration", { withCredentials: true })
      .then((res) => setHasEvidenceIntegration(!!res.data.integration))
      .catch(() => setHasEvidenceIntegration(false));
  }, []);

  useEffect(() => {
    setSuggestions(caseSuggestions || []);
  }, [caseSuggestions, setCaseSuggestions]);

  useEffect(() => {
    setScore(caseScore);
  }, [caseScore]);

  const handleToggleImplemented = async (suggestionId: string) => {
    try {
      const response = await axios.patch(
        `/api/suggestions/${suggestionId}`,
        {},
        {
          withCredentials: true,
        },
      );

      if (response.status === 200) {
        const updatedSuggestions = suggestions.map((suggestion) =>
          suggestion.id === suggestionId
            ? {
                ...suggestion,
                implemented: response.data.suggestion.implemented,
              }
            : suggestion,
        );
        setSuggestions(updatedSuggestions);
        if (setCaseSuggestions) {
          setCaseSuggestions(updatedSuggestions);
        }
      }
    } catch (error) {
      console.error("Error toggling suggestion implementation:", error);
    }
  };

  const handleEvaluate = async () => {
    if (!editorContent) return;

    setIsEvaluating(true);
    const toastId = toast.loading(
      `${hasAlreadyBeenEvaluated ? "Re-evaluating" : "Evaluating"} report...`,
      {
        duration: Infinity,
      },
    );

    try {
      const response = await axios.post(
        "/api/evaluate",
        {
          caseId,
          content: editorContent,
          previousScore: score?.score ?? null,
          previousSuggestions: suggestions.map((s) => ({
            content: s.content,
            implemented: s.implemented,
          })),
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
        // Also update the parent component's state
        if (setCaseSuggestions) {
          setCaseSuggestions(response.data.suggestions);
        }
      }

      if (response.data.score) {
        setScore(response.data.score);
        // Update the parent component's score state if available
        if (setCaseScore) {
          setCaseScore(response.data.score);
        }
      }

      toast.success(
        `Report ${
          hasAlreadyBeenEvaluated ? "re-evaluated" : "evaluated"
        } successfully`,
        {
          id: toastId,
          ...toastOptions,
        },
      );
    } catch (error) {
      checkAuthStatus();
      if (axios.isAxiosError(error)) {
        // Handle Axios-specific errors
        const errorMessage =
          error.response?.data?.error || "Error refetching suggestions";
        toast.error(errorMessage, {
          id: toastId,
        });
      } else {
        // Handle non-Axios errors
        toast.error("An unexpected error occurred");
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  const closeDropdown = useCallback(() => {
    const dropdown = dropdownRef.current?.querySelector("details");
    if (dropdown) dropdown.removeAttribute("open");
  }, []);

  // Fire-and-forget upload handler. Once the audio files are uploaded the
  // server-side completion handler (casesRouter /:caseId/files/:fileId/completed)
  // calls Bedrock with action "suggestions-insert" and persists the resulting
  // changes to cases.pendingSuggestionsChanges. The user can close the tab any
  // time after the upload finishes — the banner will pick up the pending
  // changes the next time they open the case.
  //
  // While the upload + Bedrock call is running we leave isAddingToReport=true
  // so the editor shows the "Generating changes" overlay. The parent
  // (TiptapEditor) flips it back off via a useEffect once
  // pendingSuggestionsChanges arrives.
  const handleSuggestionsUpload = useCallback(
    async (files: any[], options: any) => {
      if (!files.length || !caseId) return;

      setIsAddingToReport?.(true);
      // Only mark the upload phase as active if there's at least one file
      // that actually needs to be uploaded to S3 from the client. Previous
      // uploads reuse existing transcriptions (no upload), and Axon transfers
      // are server-side (the user can leave the moment the kickoff returns).
      // For those paths the "Generating changes / you can safely leave"
      // message is correct from the start.
      const hasClientUpload = files.some(
        (f: any) => !f?.previousUpload && !f?.axonSource,
      );
      if (hasClientUpload) {
        setIsUploadingAddToReport?.(true);
      }

      processAudioFile(
        files,
        {
          detailLevel: options?.detailLevel || "standard",
          includeTimestamps: options?.includeTimestamps || false,
        },
        undefined,
        undefined,
        {
          createNewCase: false,
          existingCaseId: caseId,
          intent: "add_to_report",
          reportTemplateId: options?.reportTemplateId || undefined,
          onError: (error: string) => {
            checkAuthStatus();
            toast.error(error || "Failed to process audio");
            setIsAddingToReport?.(false);
            setIsUploadingAddToReport?.(false);
          },
        },
      );
    },
    [
      caseId,
      setIsAddingToReport,
      setIsUploadingAddToReport,
      checkAuthStatus,
    ],
  );

  // Flip isAddingToReport off as soon as pending changes arrive (success or
  // error). The polling effect in CasesLayout drives this — it picks up the
  // server-persisted result and propagates it down through props.
  useEffect(() => {
    if (pendingSuggestionsChanges) {
      setIsAddingToReport?.(false);
    }
  }, [pendingSuggestionsChanges, setIsAddingToReport]);

  // Reset the "we've already opened this blob" guard when switching cases.
  useEffect(() => {
    lastHandledPendingCreatedAtRef.current = null;
  }, [caseId]);

  // Auto-open the review walkthrough as soon as pending changes (without an
  // error) arrive. No banner — we go straight into review. The user can
  // discard from the review dialog if they don't want the changes.
  //
  // Guarded by `lastHandledPendingCreatedAtRef`: once we've opened a review
  // for a particular blob (identified by its createdAt), we won't reopen the
  // same blob even if the prop is still set. This prevents a re-open race
  // when the user accepts or discards — the local clear runs synchronously
  // but the parent refetch is async, so for a few hundred ms the prop still
  // holds the old blob and the dep change from `isReviewing` would otherwise
  // re-fire this effect.
  useEffect(() => {
    if (
      pendingSuggestionsChanges &&
      !pendingSuggestionsChanges.error &&
      pendingSuggestionsChanges.changes?.length &&
      !isReviewing &&
      editor &&
      lastHandledPendingCreatedAtRef.current !==
        pendingSuggestionsChanges.createdAt
    ) {
      lastHandledPendingCreatedAtRef.current =
        pendingSuggestionsChanges.createdAt;
      handleStartReview();
    }
    // handleStartReview is intentionally not in the dep array — it changes
    // when pendingSuggestionsChanges does, which would otherwise cause a
    // double-fire. We want one trigger per pending blob arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSuggestionsChanges, isReviewing, editor]);

  // Open the review dialog with the changes from the persisted blob. Filters
  // out changes that can't be applied to the current editor content (e.g. the
  // user edited the report after the changes were generated).
  const handleStartReview = useCallback(() => {
    if (!pendingSuggestionsChanges?.changes?.length || !editor) return;

    const { applicable, skipped } = filterApplicableChanges(
      pendingSuggestionsChanges.changes as SuggestionChange[],
      editor,
    );

    if (applicable.length === 0) {
      toast.error(
        "Generated changes couldn't be matched to your report. Try with clearer references to the existing text.",
        toastOptions,
      );
      return;
    }

    if (skipped > 0) {
      toast(
        `${skipped} change${skipped === 1 ? "" : "s"} couldn't be applied`,
        { icon: "⚠️", ...toastOptions },
      );
    }

    setPendingChanges(applicable);
    setPendingCaseFileIds(pendingSuggestionsChanges.fileIds || []);
    setIsReviewing(true);
  }, [pendingSuggestionsChanges, editor]);

  // Discard the pending changes — clean up the source case_files (and S3
  // objects) and clear the pending blob on the case.
  const handleDiscardPending = useCallback(async () => {
    if (!caseId) return;
    try {
      await axios.post(
        "/api/transcribe/suggestions-insert-cleanup",
        {
          caseId,
          caseFileIds: pendingSuggestionsChanges?.fileIds || [],
        },
        { withCredentials: true },
      );
      toast.success("Pending changes discarded", toastOptions);
      onPendingChangesCleared?.();
    } catch (error) {
      console.error("Failed to discard pending changes:", error);
      toast.error("Failed to discard pending changes");
    }
  }, [caseId, pendingSuggestionsChanges, onPendingChangesCleared]);

  // Retry a failed Bedrock suggestions-insert call. Reuses the existing
  // case_files (no re-upload needed) — server reads the fileIds from the
  // current pending blob and replays the call.
  const [isRetryingPending, setIsRetryingPending] = useState(false);
  const handleRetryPending = useCallback(async () => {
    if (!caseId) return;
    setIsRetryingPending(true);
    // Show the "Generating changes" overlay immediately. The server clears the
    // errored blob, marks the case generating, and re-runs Bedrock in the
    // background; the result (or a fresh error) is surfaced by the poll loop —
    // we no longer hold this request open while Bedrock runs (which could exceed
    // the 60s ALB idle timeout).
    setIsAddingToReport?.(true);
    try {
      await axios.post(
        `/api/cases/${caseId}/retry-pending-suggestions-changes`,
        {},
        { withCredentials: true },
      );
      // Server already cleared the old (errored) pending blob and set
      // intent=generating_changes. Sync local state so the error banner clears;
      // polling opens the review dialog (or shows a fresh error) on its own.
      onPendingChangesCleared?.();
    } catch (error) {
      console.error("Failed to retry pending changes:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Failed to retry"
        : "Failed to retry";
      toast.error(errorMessage);
      setIsAddingToReport?.(false);
    } finally {
      setIsRetryingPending(false);
    }
  }, [caseId, onPendingChangesCleared, setIsAddingToReport]);

  const SuggestionsSkeleton = () => (
    <div className="p-4 bg-base-200 rounded-lg">
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          className="checkbox checkbox-round checkbox-xs border-base-300 mt-[5px]"
          checked={false}
          readOnly
        />
        <div className="flex-1">
          <Skeleton height={20} width="100%" />
          <Skeleton height={20} width={`${Math.random() * 40 + 60}%`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-2 md:pt-0 relative">
      {isEvaluating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <span className="loading loading-spinner loading-md text-blue-500 dark:text-blue-300" />
        </div>
      )}
      <div className={cn(isEvaluating && "opacity-35 pointer-events-none")}>
        {" "}
        <div className="flex items-center prose mb-2">
          <SparklesIcon className="size-5 text-blue-500 dark:text-blue-300" />
          <h3 className="m-0 ml-2 text-base">Suggestions</h3>
        </div>
        {/* Success-path pending changes auto-open the review walkthrough via
            the useEffect below — no banner. Only show the banner for the
            error case so the user can retry/discard. */}
        {(hasPendingError || hasPendingEmpty) && !isReviewing && (
          <PendingChangesBanner
            pendingData={pendingSuggestionsChanges!}
            onReview={handleStartReview}
            onDiscard={handleDiscardPending}
            onRetry={handleRetryPending}
            isRetrying={isRetryingPending}
          />
        )}
        {/* grid-cols-1 (= minmax(0,1fr)) rather than a bare `grid`: an auto
            track is sized by its items' min-content, so the score card and the
            action buttons made this column wider than the sidebar and spilled
            off the right edge of the screen at 150% browser zoom. */}
        <div className="grid grid-cols-1 gap-y-2 pb-24">
          {hasAlreadyBeenEvaluated && (
            <ReportScore score={score} isLoading={isCaseLoading} />
          )}
          {!isCaseLoading && caseId && editor && (
            /* min-w-0: as a grid item this would otherwise be sized by the
               widest button's min-content and spill out of a narrow sidebar. */
            <div className="flex flex-col gap-1.5 mt-1 min-w-0">
              <div
                ref={dropdownRef}
                data-tour="add-to-report-button"
                className="dropdown w-full"
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    closeDropdown();
                  }
                }}
              >
                <Tooltip
                  overlay={
                    isAddToReportInFlight
                      ? "Generating changes — please wait or cancel"
                      : hasPendingChanges
                        ? "Review pending changes first"
                        : hasPendingError
                          ? "Discard the failed batch first"
                          : hasPendingEmpty
                            ? "Discard the previous batch first"
                            : "Record or upload audio to add details, fill in placeholders, and address suggestions"
                  }
                  placement="top"
                >
                  <Button
                    tabIndex={0}
                    variant="primary"
                    size="sm"
                    className="w-full"
                    disabled={
                      isEvaluating ||
                      isReviewing ||
                      isAddingToReport ||
                      isAddToReportInFlight ||
                      hasPendingChanges ||
                      hasPendingError ||
                      hasPendingEmpty
                    }
                  >
                    <SparklesIcon className="size-4 hidden 2xl:inline-block" />
                    Address Suggestions
                  </Button>
                </Tooltip>
                <ul
                  tabIndex={0}
                  className="dropdown-content menu bg-base-100 rounded-lg z-20 w-full p-2 shadow mt-1"
                >
                  <li>
                    <button
                      onClick={() => {
                        closeDropdown();
                        recordModalRef.current?.showModal();
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-base-200"
                    >
                      <MicrophoneIcon className="size-4" />
                      Record Audio
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        closeDropdown();
                        uploadModalRef.current?.showModal();
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-base-200"
                    >
                      <ArrowUpTrayIcon className="size-4" />
                      Upload Audio
                    </button>
                  </li>
                </ul>
              </div>
              {hasAlreadyBeenEvaluated && (
                <Tooltip
                  overlay={
                    isAddToReportInFlight
                      ? "Generating changes — please wait or cancel"
                      : "Re-run AI evaluation to get updated suggestions and score"
                  }
                  placement="bottom"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={handleEvaluate}
                    data-tour="re-evaluate"
                    loading={isEvaluating}
                    disabled={
                      isEvaluating ||
                      !editorContent ||
                      isReviewing ||
                      isAddingToReport ||
                      isAddToReportInFlight
                    }
                  >
                    <DocumentMagnifyingGlassIcon className="size-5 hidden 2xl:inline-block" />
                    Re-evaluate
                  </Button>
                </Tooltip>
              )}
              {onTogglePlaceholdersHidden && placeholders.length > 0 && (
                <div data-tour="hide-placeholders" className="border border-base-300 rounded-lg p-3 mt-1 bg-base-100">
                  <Toggle
                    layout="compact"
                    id="suggestions-hide-placeholders"
                    label="Hide placeholders"
                    checked={!!placeholdersHidden}
                    onChange={onTogglePlaceholdersHidden}
                  />
                </div>
              )}
            </div>
          )}
          {isCaseLoading ? (
            Array.from({ length: 6 }, (_, index) => (
              <SuggestionsSkeleton key={index} />
            ))
          ) : suggestions.length > 0 ? (
            suggestions.map(({ id, implemented, content }) => {
              // "[Policy check · Manual § 340.5] …" renders the prefix as badges
              // under the text; plain suggestions are unchanged.
              const { text, tag } = parseSuggestionTag(content);
              return (
                <div
                  className="form-control"
                  key={id}
                  data-tour="suggestion-item"
                >
                  <label className="label flex items-start justify-start p-4 text-sm cursor-pointer transition duration-100 border-b-2 bg-base-200 dark:bg-[#2a323c] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/40 border-transparent">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-round checkbox-xs mt-[5px] peer border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:theme(colors.blue.50)] checked:border-blue-500"
                      checked={implemented || false}
                      onChange={() => handleToggleImplemented(id)}
                    />
                    <div className="ml-2 min-w-0 flex-1 peer-checked:[&>p]:line-through peer-checked:[&>p]:text-base-content/60 peer-checked:[&_span]:opacity-60">
                      <p className="mt-[1px] label-text text-xs 2xl:text-sm text-gray-600 dark:text-base-content/70 transition-colors duration-200 hover:text-blue-700 dark:hover:text-blue-300">
                        {text}
                      </p>
                      {tag && (
                        <SuggestionTagBadges
                          tag={tag}
                          className="mt-2"
                          onOpenPolicy={onOpenPolicy}
                          policyReferences={policyReferences}
                        />
                      )}
                    </div>
                  </label>
                </div>
              );
            })
          ) : (
            <p className="text-sm my-4">
              Get AI recommendations and a report score to improve your report.
            </p>
          )}
          {!isCaseLoading && !hasAlreadyBeenEvaluated && (
            <Button
              variant="primary"
              className="w-full mb-24"
              onClick={handleEvaluate}
              loading={isEvaluating}
              disabled={
                isEvaluating ||
                !editorContent ||
                isReviewing ||
                isAddToReportInFlight
              }
            >
              Evaluate Report
            </Button>
          )}
        </div>
      </div>

      {/* Suggestions review bar (shown during review walk-through) */}
      {isReviewing && pendingChanges.length > 0 && editor && (
        <SuggestionsReview
          changes={pendingChanges}
          editor={editor}
          onSaveEditor={onSaveEditor}
          setIsReviewingChanges={setIsReviewingChanges}
          onReviewDecisions={(decisions) => {
            // Record which proposed changes the officer accepted vs discarded.
            // Fire-and-forget — auditing must never block finishing the review.
            if (!caseId) return;
            axios
              .post(
                `/api/cases/${caseId}/suggestions-review-audit`,
                { decisions },
                { withCredentials: true },
              )
              .catch((err) => {
                console.error(
                  "Failed to record suggestions review audit:",
                  err,
                );
              });
          }}
          onComplete={async () => {
            // Save the final editor state with all accepted changes
            onSaveEditor?.();
            setPendingChanges([]);
            setPendingCaseFileIds([]);
            setIsReviewing(false);

            // Clear the pending blob server-side now that the user accepted
            // the changes. The case_files rows themselves are kept (the audio
            // is part of the case history).
            if (caseId) {
              axios
                .post(
                  `/api/cases/${caseId}/clear-pending-suggestions-changes`,
                  {},
                  { withCredentials: true },
                )
                .catch((err) => {
                  console.error(
                    "Failed to clear pending_suggestions_changes after accept:",
                    err,
                  );
                });
            }

            // Re-evaluate the report and refresh case data (transcriptions, files, etc.)
            await handleEvaluate();
            onPendingChangesCleared?.();
            onRecordComplete?.();
          }}
          onCancel={() => {
            // onCancel fires when the user picks "Discard All" — the snapshot has
            // been restored to its pre-review state, so no trace of the recording
            // remains in the report. Clean up the S3 files, case_files rows,
            // and the pending blob in one server call.
            if (caseId) {
              axios
                .post(
                  "/api/transcribe/suggestions-insert-cleanup",
                  {
                    caseId,
                    caseFileIds: pendingCaseFileIds,
                  },
                  { withCredentials: true },
                )
                .catch((err) => {
                  console.error(
                    "Failed to clean up discarded suggestions-insert:",
                    err,
                  );
                });
            }
            setPendingChanges([]);
            setPendingCaseFileIds([]);
            setIsReviewing(false);
            onPendingChangesCleared?.();
          }}
        />
      )}

      {/* AudioPlayback modal in suggestions mode */}
      <AudioPlayback
        modalRef={recordModalRef as React.RefObject<HTMLDialogElement>}
        handleAudioFileChange={async () => {}}
        suggestionsMode
        suggestions={suggestions}
        placeholders={placeholders}
        onSuggestionsRecorded={(wavBlob, reportTemplateId) => {
          // Wrap the recorded blob in a FileConfig and route through the same
          // unified upload pipeline as the upload-modal flow.
          const recordingFile = new File(
            [wavBlob],
            "suggestions-recording.wav",
            {
              type: "audio/wav",
            },
          );
          handleSuggestionsUpload(
            [
              {
                file: recordingFile,
                audioType: "narrative",
                speakers: [],
                languages: [],
                additionalContext: "",
              },
            ],
            {
              reportTemplateId,
              detailLevel: "standard",
              includeTimestamps: false,
            },
          );
        }}
      />

      {/* UploadModal for suggestions mode */}
      <UploadModal
        modalRef={uploadModalRef as React.RefObject<HTMLDialogElement>}
        onUpload={handleSuggestionsUpload}
        hasEvidenceIntegration={hasEvidenceIntegration}
        hideReportSettings
        suggestionsList={suggestions}
        placeholdersList={placeholders}
      />
    </div>
  );
}

export default Suggestions;
